import { array, option } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { createSelector } from 'reselect';

import { getProp, getStringProp, values } from '@mews/fp';
import { AppClockServiceProps, WallClockDate, WallClockDateService } from '@mews/framework/datetime';

import { getMinLengthDateSuggestions } from '../../../modules/entities/restrictions/restrictionSuggestions';
import { getSomeRestrictions } from '../../../modules/entities/restrictions/selectors';
import {
    AggregatedExceptions,
    aggregateRestrictions,
    keepGlobalRestrictions,
} from '../../../modules/entities/restrictions/violatedRestrictions';
import { isServiceHourly } from '../../../modules/entities/services/selectors';
import { localizeDate } from '../../../modules/globalization/selectors';
import { getCurrentDateAvailabilityViolatedRestrictionIds } from '../../../selectors/availability';
import { getMinimalStartDate } from '../../../selectors/views/dates';
import { ApplicationState } from '../../../types';
import { getOtherDatesSuggestions } from '../getOtherDatesSuggestions';

const getCurrentDateViolatedRestrictions = (state: ApplicationState) => {
    const restrictionIds = getCurrentDateAvailabilityViolatedRestrictionIds(state);

    return getSomeRestrictions(state, { restrictionIds });
};

const getCurrentDateAggregatedExceptions = flow(
    getCurrentDateViolatedRestrictions,
    keepGlobalRestrictions,
    aggregateRestrictions
);

export interface AggregatedExceptionsWithDates extends AggregatedExceptions {
    minAllowedDateString: string | null;
    maxAllowedDateString: string | null;
}

export const getCurrentDateAggregatedExceptionsWithDates = (
    state: ApplicationState,
    { appClockService }: AppClockServiceProps
) => {
    const currentDateAggregatedExceptions = getCurrentDateAggregatedExceptions(state);
    const nowUtc = appClockService.getAppNowUtc();
    const today = getMinimalStartDate(state, { nowUtc });

    const addDaysAndLocalizeDate = (daysDiff: number | null) => {
        if (daysDiff == null) {
            return null;
        }
        const date = WallClockDateService.addDays(today, daysDiff);
        return localizeDate(state, { date });
    };

    return pipe(
        currentDateAggregatedExceptions,
        option.map((restriction) => ({
            ...restriction,
            minAllowedDateString: addDaysAndLocalizeDate(restriction.minAdvanceDays),
            maxAllowedDateString: addDaysAndLocalizeDate(restriction.maxAdvanceDays),
        }))
    );
};

export const hasCurrentDateExceptionsApplied = createSelector(
    getCurrentDateAggregatedExceptions,
    flow(
        option.map(
            flow(
                values,
                array.some((exception) => exception !== null)
            )
        ),
        option.getOrElse(() => false)
    )
);

export const getCurrentAlternativeSuggestions = createSelector(
    getStringProp('startDate'),
    getStringProp('endDate'),
    getProp<WallClockDate>()('todayDate'),
    getCurrentDateAggregatedExceptions,
    isServiceHourly,
    (startDateString, endDateString, todayDate, currentRestrictions, isHourlyService) =>
        pipe(
            currentRestrictions,
            option.map(({ minLengthDays }) => {
                const startDate = WallClockDateService.parse(startDateString);
                const endDate = WallClockDateService.parse(endDateString);
                if (minLengthDays !== null) {
                    return getMinLengthDateSuggestions({
                        startDate,
                        endDate,
                        minLengthDays,
                        todayDate,
                    });
                }
                return getOtherDatesSuggestions({
                    startDate: startDateString,
                    endDate: endDateString,
                    minimalStartDate: todayDate,
                    isHourlyService,
                });
            }),
            option.getOrElse(() =>
                getOtherDatesSuggestions({
                    startDate: startDateString,
                    endDate: endDateString,
                    minimalStartDate: todayDate,
                    isHourlyService,
                })
            )
        )
);
